import {
  ChangeDetectionStrategy,
  Component,
  ElementRef,
  booleanAttribute,
  computed,
  input,
  viewChild,
} from '@angular/core';
import { UiButtonDirective } from '../button/button.directive';
import { UiIconComponent } from '../icon/icon.component';

export type UiIconButtonAppearance = 'solid' | 'outline' | 'ghost';
export type UiIconButtonTone = 'primary' | 'accent' | 'neutral' | 'danger';
export type UiIconButtonSize = 'sm' | 'md';

/**
 * Circular icon-only button. Composes appUiButton (appearance/tone/focus/hover)
 * and centralizes the square-circular sizing that feature templates previously
 * hand-rolled as `!h-8 !min-h-8 !w-8 !min-w-8 !rounded-full !p-0`. An aria-label
 * is required since there is no visible text. Bind (click) on the host — the
 * inner button's click bubbles up.
 */
@Component({
  selector: 'app-ui-icon-button',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [UiButtonDirective, UiIconComponent],
  template: `
    <button
      #button
      appUiButton
      [appearance]="appearance()"
      [tone]="tone()"
      [type]="type()"
      [disabled]="disabled()"
      [attr.aria-label]="ariaLabel()"
      [class]="buttonClasses()"
    >
      <span class="inline-flex h-6 w-6 items-center justify-center" aria-hidden="true">
        <app-ui-icon [name]="icon()" [set]="iconSet()" />
      </span>
    </button>
  `,
})
export class UiIconButtonComponent {
  readonly icon = input.required<string>();
  readonly iconSet = input<'app' | 'budget'>('app');
  readonly ariaLabel = input.required<string>();
  readonly appearance = input<UiIconButtonAppearance>('ghost');
  readonly tone = input<UiIconButtonTone>('neutral');
  readonly size = input<UiIconButtonSize>('md');
  readonly type = input<'button' | 'submit'>('button');
  readonly disabled = input(false, { transform: booleanAttribute });

  private readonly button = viewChild.required<ElementRef<HTMLButtonElement>>('button');

  /** Moves keyboard focus to the button, e.g. when a control it opened closes. */
  focus(): void {
    this.button().nativeElement.focus();
  }

  protected readonly buttonClasses = computed(() => `${SIZE_CLASSES[this.size()]} !rounded-full !p-0`);
}

const SIZE_CLASSES: Record<UiIconButtonSize, string> = {
  sm: '!h-8 !min-h-8 !w-8 !min-w-8',
  md: '!h-9 !min-h-9 !w-9 !min-w-9',
};
